import React, { useMemo, useState, useEffect } from 'react';
import { Refresh, HeadsetMic, VerifiedUser } from '@material-ui/icons';
import { useAppSelector } from '../../hooks';
import { currentUserAuthState } from '../../selectors';
import { UserAuthState } from '../../enums';
import { API_BASEURL } from '../../config';
import { Clickable, ClickableVariant } from '../../components/Clickable';
import { c } from '../../utils';
import classes from './BillingErrorPage.module.scss';
import { getItem } from '../../utils/fetchLinks';
import { CONTACT_HELP_URL, PRICING_URL } from '../../config';

// [2026-05-18] Query param `?reason=...` allows the frontend to tailor the error copy.
export type BillingErrorReason = 'checkout_failed' | 'billing_portal_failed';

type CardCopy = { title: string; body: string };

// [2026-05-18] Source of truth for tailored error copy. Supports dual-color headings and context-aware cards.
const reasonCopy: Record<
  BillingErrorReason,
  {
    titlePrimary: string;
    titleAccent?: string;
    lead: string;
    tryAgain: CardCopy;
    contact: CardCopy;
    security: CardCopy;
  }
> = {
  checkout_failed: {
    titlePrimary: 'Our payment gateway is experiencing a brief disruption',
    titleAccent: ", but don't worry—your card was not charged",
    lead: "Let's try that again, or feel free to contact us if the issue persists.",
    tryAgain: {
      title: 'Retry Checkout',
      body: "Click here to safely restart the process. Double charges aren't possible.",
    },
    contact: {
      title: 'Contact Support',
      body: 'Still having trouble? Reach out to our team, and we can manually process your order.',
    },
    security: {
      title: 'System Security',
      body: 'No transaction took place. Your card remains untouched and completely safe.',
    },
  },
  billing_portal_failed: {
    titlePrimary: "We couldn't open your billing admin page right now",
    titleAccent: ', but your plan remains exactly the same.',
    lead: 'No changes or charges were made to your account.',
    tryAgain: {
      title: 'Retry Access',
      body: 'Something went wrong with the payment provider. Please review your details and try again shortly.',
    },
    contact: {
      title: 'Contact Support',
      body: 'Get in touch, and we will manually verify your account status.',
    },
    security: {
      title: 'Account Security',
      body: 'Your account remains secure. Your historical data is safe and has not been altered.',
    },
  },
};

const parseReason = (search: string): BillingErrorReason => {
  const value = new URLSearchParams(search).get('reason');
  if (value === 'checkout_failed' || value === 'billing_portal_failed') {
    return value;
  }
  return 'checkout_failed';
};

const parsePlan = (search: string): string | null => {
  const value = new URLSearchParams(search).get('plan');
  return value || null;
};

export function BillingErrorPage() {
  const authState = useAppSelector(currentUserAuthState);
  const isLoggedIn = authState === UserAuthState.LOGGED_IN;
  // [2026-06-12] Replaces static CONTACT_US_URL/PUBLIC_PRICING_URL constants with dynamic em-links.json state for zero-deploy runtime updates.
  const [contactUsUrl, setContactUsUrl] = useState(CONTACT_HELP_URL);
  const [publicPricingUrl, setPublicPricingUrl] = useState(PRICING_URL);

  useEffect(() => {
    Promise.all([
      getItem('configUrls', 'Contact Help'),
      getItem('configUrls', 'Pricing'),
    ])
      .then(([contactUrl, pricingUrl]) => {
        if (contactUrl) setContactUsUrl(contactUrl);
        if (pricingUrl) setPublicPricingUrl(pricingUrl);
      })
      .catch(() => {});
  }, []);

  // [2026-05-18] Read on mount; uses window.location.search because wouter drops query params.
  const reason = useMemo(() => parseReason(window.location.search), []);
  const plan = useMemo(() => parsePlan(window.location.search), []);
  const { titlePrimary, titleAccent, lead, tryAgain, contact, security } =
    reasonCopy[reason];

  // [2026-05-18] "Retry" target routing logic:
  // - billing_portal_failed -> /account (retry management)
  // - Guest + plan -> re-trigger public checkout endpoint (fresh Stripe 302)
  // - Guest - plan -> gatherly.io pricing page (avoids out-of-context in-app routes)
  // - Authed user -> /pricing (links session to existing Stripe customer to prevent duplicates)
  const tryAgainHref = useMemo(() => {
    if (reason === 'billing_portal_failed') return '/account';
    if (!isLoggedIn) {
      if (plan) {
        return `${API_BASEURL}/billing/public-checkout?plan=${encodeURIComponent(
          plan,
        )}`;
      }
      return publicPricingUrl;
    }
    return '/pricing';
  }, [reason, isLoggedIn, plan, publicPricingUrl]);

  return (
    <div
      className={c('page-inner', classes.BillingErrorPage)}
      data-cy="BillingErrorPage"
    >
      <div className={c(classes.inner, 'flex flex-col gap-2 p2')}>
        <div className="flex flex-col gap-1">
          <h2 className={c(classes.heading, 'heading')}>
            <span className={classes.headingPrimary}>{titlePrimary}</span>
            {titleAccent && (
              <span className={classes.headingAccent}>{titleAccent}</span>
            )}
          </h2>
          <p className={c(classes.lead, 'body')}>{lead}</p>
        </div>

        <div className={classes.cards}>
          <Clickable
            variant={ClickableVariant.NONE}
            to={tryAgainHref}
            className={c(classes.card, classes.cardLink)}
          >
            <Refresh className={classes.cardIcon} />
            <div className={classes.cardTitle}>{tryAgain.title}</div>
            <p className={classes.cardBody}>{tryAgain.body}</p>
          </Clickable>

          <Clickable
            variant={ClickableVariant.NONE}
            to={contactUsUrl}
            className={c(classes.card, classes.cardLink)}
          >
            <HeadsetMic className={classes.cardIcon} />
            <div className={classes.cardTitle}>{contact.title}</div>
            <p className={classes.cardBody}>{contact.body}</p>
          </Clickable>

          <div className={classes.card}>
            <VerifiedUser className={classes.cardIcon} />
            <div className={classes.cardTitle}>{security.title}</div>
            <p className={classes.cardBody}>{security.body}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
